<script setup lang="ts">
import { computed, useTemplateRef, watch } from "vue";

import { Counter } from "@/model/Counter";
import type { Statistics } from "@/model/baseTypes";
import { isNonPrimitive } from "@/utils/type";

const props = defineProps<{ stats: Statistics[]; property: keyof Statistics; diff?: boolean }>();

const simpleColors = ["0,127,127"];
const counterColors = ["0,0,255", "0,0,127"];

const canvasRef = useTemplateRef<HTMLCanvasElement>("canvas");
const context = computed(() => canvasRef.value?.getContext("2d"));

watch(() => props.stats.length, draw);

function val(i: number) {
  return props.stats[i][props.property];
}

function eff(value: number, last: number) {
  return props.diff ? value - last : value;
}

function draw() {
  const ctx = context.value;
  if (!ctx) {
    return;
  }
  ctx.clearRect(0, 0, 100, 100);
  if (props.stats.length > 0) {
    const end = props.stats.length;
    const start = Math.max(0, end - 250);
    const firstVal = val(0);
    const counter = isNonPrimitive(firstVal) && "ok" in firstVal;
    const colors = counter ? counterColors : simpleColors;
    let max = 1;
    let lastN = 0;
    let lastC = new Counter();
    for (let i = start; i < end; i++) {
      const v = val(i);
      if (counter) {
        const vc = v as Counter;
        max = Math.max(max, eff(vc.ok, lastC.ok), eff(vc.fail, lastC.fail));
        lastC = vc;
      } else {
        const vn = v as number;
        max = Math.max(max, eff(vn, lastN));
        lastN = vn;
      }
    }

    const xf = 100 / (end - start);
    for (let r = 0; r < (counter ? 2 : 1); r++) {
      ctx.beginPath();
      ctx.moveTo(0, 100);
      let last = 0;
      for (let i = start; i < end; i++) {
        const raw = val(i);
        let v: number;
        if (counter) {
          const vc = raw as Counter;
          v = r === 0 ? vc.ok : vc.fail;
        } else {
          v = raw as number;
        }
        ctx.lineTo((i - start) * xf, 100 - (eff(v, last) * 100) / max);
        last = v;
      }
      ctx.lineTo((end - 1 - start) * xf, 100);
      ctx.closePath();
      ctx.fillStyle = `rgba(${colors[r]},.5)`;
      ctx.fill();
    }
  }
}
</script>

<template>
  <canvas ref="canvas" width="100" height="100" class="graph" data-no-screenshot />
</template>

<style lang="scss">
.graph {
  width: 100%;
  height: 100%;
}
</style>
